import StudentLearningOverview from '../components/StudentLearningOverview';
import { historyAccess, historyLevels } from '../../../shared/historyScope';
import type { UserExamRestrictions } from '../utils/api';

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import { api } from '../utils/api';
import { ArrowLeft, BookOpen, ChevronLeft, ChevronRight, Filter, Trash2, RotateCcw, Clock } from 'lucide-react';
import { isAdminRole } from '../utils/permissions';

interface VocabItem {
  vocab_id: string;
  hanzi: string;
  pinyin: string;
  En_meaning: string;
  proficiency: number;
  appear: number;
  correct: number;
  level: number;
}

const VocabDistributionChart: React.FC<{ red: number; yellow: number; green: number; total: number }> = ({ red, yellow, green, total }) => {
  if (total === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 bg-gray-50 rounded-lg">
        <p className="text-gray-500">尚无已练习词汇，暂未评估</p>
      </div>
    );
  }

  const score = Math.round((green / total) * 100);
  
  // Calculate SVG stroke-dasharray for donut segments
  // Radius = 40 (arbitrary relative unit), Circumference = 2 * PI * 40 ≈ 251.2
  const r = 40;
  const c = 2 * Math.PI * r;
  
  // Lengths
  const lGreen = (green / total) * c;
  const lYellow = (yellow / total) * c;
  const lRed = (red / total) * c;
  
  // Offsets (starting from top -90deg, clockwise)
  // Green starts at 0 (top)
  // Yellow starts after Green
  // Red starts after Yellow
  
  // SVG circles stack:
  // We can draw 3 circles with dashes.
  // Circle 1 (Green): Dash = [lGreen, c - lGreen], Offset = 0
  // Circle 2 (Yellow): Dash = [lYellow, c - lYellow], Offset = -lGreen
  // Circle 3 (Red): Dash = [lRed, c - lRed], Offset = -(lGreen + lYellow)
  
  // But to handle empty space correctly if any segment is 0, this logic holds.
  // Note: SVG stroke-dashoffset is counter-clockwise for positive values usually?
  // Let's check standard SVG behavior: positive offset pushes dash START back.
  // We want clockwise filling.
  // transform="rotate(-90 50 50)" puts start at 12 o'clock.
  
  return (
    <div className="bg-white shadow rounded-lg p-6 flex flex-col items-center justify-center h-full">
      <h3 className="text-lg font-medium text-gray-900 mb-4 w-full text-left">Vocabulary Proficiency</h3>
      <div className="relative w-48 h-48">
        <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
          {/* Background Circle (optional, maybe light gray track) */}
          <circle cx="50" cy="50" r={r} fill="none" stroke="#f3f4f6" strokeWidth="12" />
          
          {/* Green Segment */}
          {green > 0 && (
            <circle
              cx="50"
              cy="50"
              r={r}
              fill="none"
              stroke="#22c55e" // green-500
              strokeWidth="12"
              strokeDasharray={`${lGreen} ${c}`}
              strokeDashoffset="0"
            />
          )}
          
          {/* Yellow Segment */}
          {yellow > 0 && (
            <circle
              cx="50"
              cy="50"
              r={r}
              fill="none"
              stroke="#eab308" // yellow-500
              strokeWidth="12"
              strokeDasharray={`${lYellow} ${c}`}
              strokeDashoffset={-lGreen}
            />
          )}
          
          {/* Red Segment */}
          {red > 0 && (
            <circle
              cx="50"
              cy="50"
              r={r}
              fill="none"
              stroke="#ef4444" // red-500
              strokeWidth="12"
              strokeDasharray={`${lRed} ${c}`}
              strokeDashoffset={-(lGreen + lYellow)}
            />
          )}
        </svg>
        
        {/* Center Text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-4xl font-bold text-gray-900">{score}%</span>
          <span className="text-sm text-gray-500">已掌握比例</span>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 w-full grid grid-cols-3 gap-2 text-center text-xs">
        <div>
          <div className="flex items-center justify-center mb-1">
            <div className="w-3 h-3 rounded-full bg-green-500 mr-1"></div>
            <span className="font-medium">{Math.round((green/total)*100)}%</span>
          </div>
          <div className="text-gray-500">已掌握（&gt;10）</div>
          <div className="text-gray-400">({green})</div>
        </div>
        <div>
          <div className="flex items-center justify-center mb-1">
            <div className="w-3 h-3 rounded-full bg-yellow-500 mr-1"></div>
            <span className="font-medium">{Math.round((yellow/total)*100)}%</span>
          </div>
          <div className="text-gray-500">待巩固（0–10）</div>
          <div className="text-gray-400">({yellow})</div>
        </div>
        <div>
          <div className="flex items-center justify-center mb-1">
            <div className="w-3 h-3 rounded-full bg-red-500 mr-1"></div>
            <span className="font-medium">{Math.round((red/total)*100)}%</span>
          </div>
          <div className="text-gray-500">陌生（&lt;0）</div>
          <div className="text-gray-400">({red})</div>
        </div>
      </div>
    </div>
  );
};

const AdminStudentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user: currentUser } = useAuthStore();
  const isAdmin = isAdminRole(currentUser?.role);
  
  // User info passed from list (optional)
  const user = location.state?.user;
  const returnTo = /^\/admin(?:\?|$)/.test(location.state?.returnTo || '') ? location.state.returnTo : '/admin';

  const [detailTab, setDetailTab] = useState<'overview' | 'vocab'>('overview');
  const [examRestrictions, setExamRestrictions] = useState<UserExamRestrictions | null>(null);
  const allowedVocabVersions = examRestrictions ? historyAccess(examRestrictions).families.filter(f => f !== 'CSCA').map(f => f[0]) : [];
  const [vocabList, setVocabList] = useState<VocabItem[]>([]);
  const [vocabStats, setVocabStats] = useState<{ red: number; yellow: number; green: number; total: number } | null>(null);
  const [vocabLoading, setVocabLoading] = useState(false);
  const [vocabError, setVocabError] = useState('');
  const [vocabReloadKey, setVocabReloadKey] = useState(0);
  const [overviewReloadKey, setOverviewReloadKey] = useState(0);
  const vocabCache = useRef(new Map<string, { at: number; value: any }>());
  const refreshVocabulary = () => { vocabCache.current.clear(); setVocabReloadKey(value => value + 1); setPage(1); };
  
  // Pagination
  const [page, setPage] = useState(1);
  const limit = 5;
  const [totalVocab, setTotalVocab] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  // Reset Modal
  const [showResetModal, setShowResetModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [includeVocab, setIncludeVocab] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);

  // Vocab Proficiency Setting State
  const [profVersion, setProfVersion] = useState('2.0');
  const [profLevels, setProfLevels] = useState<string[]>([]);
  const [profValue, setProfValue] = useState('');
  const [profLoading, setProfLoading] = useState(false);

  useEffect(() => {
    if (!examRestrictions) return;
    const versions = historyAccess(examRestrictions).families.filter(f => f !== 'CSCA');
    if (versions.length) setProfVersion(versions[0]);
    setSelectedVersions(versions.slice(0, 1).map(f => f[0]));
    setSelectedLevels([]); setProfLevels([]); setPage(1);
  }, [examRestrictions]);

  const toggleProfLevel = (lvl: string) => {
    setProfLevels(prev => prev.includes(lvl) ? prev.filter(l => l !== lvl) : [...prev, lvl]);
  };

  const handleSetProficiency = async () => {
      if (!profValue || profLevels.length === 0) return;
      try {
          setProfLoading(true);
          const res = await api.setAdminUserVocabProficiency(id!, profVersion, profLevels, Number(profValue), token!);
          if (res.success) {
              alert(`Successfully updated ${res.updated} vocabulary items.`);
              refreshVocabulary();
          } else {
              alert(res.message || 'Failed to update proficiency');
          }
      } catch (e: any) {
          console.error(e);
          alert(e.message || 'Error updating proficiency');
      } finally {
          setProfLoading(false);
      }
  };

  const toggleVersion = (v: string) => {
    setSelectedVersions([v]);
    setProfVersion(v + '.0');
    setProfLevels([]);
    setSelectedLevels([]);
    setVocabStats(null);
    setVocabList([]);
    setPage(1);
  };

  const toggleLevel = (l: string) => {
    setSelectedLevels(prev => prev.includes(l) ? prev.filter(i => i !== l) : [...prev, l]);
    setPage(1);
  };

  const handleReset = async () => {
    if (!adminPassword) {
        setResetError('Please enter admin password');
        return;
    }
    setResetLoading(true);
    setResetError('');
    try {
        const res = await api.resetAdminUserTests(id!, adminPassword, includeVocab, token!);
        if (res.success) {
            setShowResetModal(false);
            setAdminPassword('');
            setIncludeVocab(false);
            // Reload data
            refreshVocabulary();
            setOverviewReloadKey(value => value + 1);
            alert('Test records deleted successfully');
        } else {
            setResetError((res as any).message || 'Failed to delete records');
        }
    } catch (err: any) {
        setResetError(err.message || 'Failed to delete records');
    } finally {
        setResetLoading(false);
    }
  };

  const handleRecalc = async () => {
    if (!confirm('重算当前学生的考试汇总？只处理当前学生的记录。')) return;
    try {
      setRecalcLoading(true);
      await api.recalcAdminStats(id!, token!);
      setOverviewReloadKey(value => value + 1);
      alert('Stats recalculated successfully');
    } catch (err) {
      console.error('Failed to recalc stats', err);
      alert('Failed to recalculate stats');
    } finally {
      setRecalcLoading(false);
    }
  };

  useEffect(() => {
    if (detailTab !== 'vocab' || !token || !id || !examRestrictions || !selectedVersions.length) return;
    let active = true;
    const levels = selectedLevels.length ? selectedLevels : historyLevels(examRestrictions, selectedVersions[0] + '.0').map(String);
    const cached = async (key: string, fetcher: () => Promise<any>) => {
      const saved = vocabCache.current.get(key);
      if (saved && Date.now() - saved.at < 60000) return saved.value;
      const value = await fetcher();
      if (active) vocabCache.current.set(key, { at: Date.now(), value });
      return value;
    };
    const scopeKey = JSON.stringify([id, token, selectedVersions[0], levels, vocabReloadKey]);
    setVocabLoading(true); setVocabError('');
    Promise.all([
      cached(scopeKey + ':stats', () => api.getAdminUserVocabStats(id, token, selectedVersions[0] + '.0') ),
      cached(scopeKey + ':' + page, () => api.getAdminUserVocab(id, token, page, limit, selectedVersions, levels)),
    ]).then(([stats, res]) => {
      if (!active) return;
      if (!stats.success || !res.success) throw new Error('词汇数据读取失败，请重试。');
      const validPage = Math.min(page, Math.max(1, res.totalPages));
      if (validPage !== page) { setPage(validPage); return; }
      setVocabStats(stats.stats); setVocabList(res.vocab); setTotalVocab(res.total); setTotalPages(res.totalPages);

    }).catch(err => { if (active) setVocabError(err.message || '词汇数据读取失败，请重试。'); })
      .finally(() => { if (active) setVocabLoading(false); });
    return () => { active = false; };
  }, [detailTab, id, token, page, selectedVersions, selectedLevels, examRestrictions, vocabReloadKey]);


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center">
          <button 
            aria-label="返回学生列表"
            onClick={() => navigate(returnTo)}
            className="mr-4 p-2 rounded-full hover:bg-gray-100 text-gray-600"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {user?.username ? `${user.username}'s Details` : 'Student Details'}
            </h1>
            <p className="text-gray-500 text-sm">
              User ID: {id} • {user?.email}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => navigate(`/admin/users/${id}/history`, { state: { user, returnTo } })}
            className="flex items-center rounded-md bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700"
          >
            <Clock className="mr-2 h-4 w-4" />
            View All Test History
          </button>
          {isAdmin && (
          <>
          <button
            onClick={handleRecalc}
            disabled={recalcLoading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <RotateCcw className={`w-4 h-4 mr-2 ${recalcLoading ? 'animate-spin' : ''}`} />
            重算当前学生
          </button>
          <button
            onClick={() => setShowResetModal(true)}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete User Exam Records
          </button>
          </>
          )}
        </div>
      </div>

      <nav aria-label="学生详情分类" className="flex gap-6 border-b border-slate-200 text-sm"><button aria-current={detailTab === 'overview' ? 'page' : undefined} onClick={() => setDetailTab('overview')} className={`border-b-2 py-3 font-medium ${detailTab === 'overview' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500'}`}>学习概览</button><button onClick={() => navigate(`/admin/users/${id}/history`, {state:{user, returnTo}})} className="py-3 text-slate-500">历史记录</button>{allowedVocabVersions.length > 0 && <button aria-current={detailTab === 'vocab' ? 'page' : undefined} onClick={() => setDetailTab('vocab')} className={`border-b-2 py-3 font-medium ${detailTab === 'vocab' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500'}`}>词汇掌握</button>}</nav>
      <div hidden={detailTab !== 'overview'}>
      {token && id && <StudentLearningOverview studentId={id} token={token} user={user} onRestrictions={setExamRestrictions} refreshKey={overviewReloadKey} returnTo={returnTo} />}
      </div>
      <div hidden={detailTab !== 'vocab'} className="space-y-6">
      <nav aria-label="词汇学习体系" className="flex flex-wrap gap-2">{allowedVocabVersions.map(version => <button key={version} aria-pressed={selectedVersions[0] === version} onClick={() => toggleVersion(version)} className={`rounded-lg border px-4 py-2 ${selectedVersions[0] === version ? 'bg-indigo-600 text-white' : 'bg-white text-indigo-700'}`}>HSK {version}.0</button>)}</nav>
      <p className="text-sm text-gray-500">仅根据当前体系中已练习的词汇评估掌握情况。未练习的词不参与计算。</p>
      {vocabError && <div role="alert" className="rounded-lg bg-red-50 p-4 text-red-700">{vocabError}<button className="ml-3 underline" onClick={refreshVocabulary}>重试</button></div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Vocab Stats Chart */}
        {vocabStats && allowedVocabVersions.length > 0 && (
          <div className="md:col-span-1 h-full">
            <VocabDistributionChart {...vocabStats} />
          </div>
        )}

        {/* Vocab Proficiency Setting */}
        {isAdmin && allowedVocabVersions.length > 0 && (
        <div className="md:col-span-1 bg-white shadow rounded-lg p-6 flex flex-col h-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Vocab Proficiency Setting</h3>
            
            <div className="space-y-4 flex-1">
                <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">HSK VERSION</label>
                    <select 
                        value={profVersion}
                        onChange={(e) => { setProfVersion(e.target.value); setProfLevels([]); }}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                        {allowedVocabVersions.includes('2') && <option value="2.0">HSK 2.0</option>}
                        {allowedVocabVersions.includes('3') && <option value="3.0">HSK 3.0</option>}
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">HSK LEVELS</label>
                    <div className="grid grid-cols-3 gap-2">
                        {(examRestrictions ? historyLevels(examRestrictions, profVersion).map(String) : []).map(lvl => (
                            <button
                                key={lvl}
                                onClick={() => toggleProfLevel(lvl)}
                                className={`px-2 py-1.5 text-sm font-medium rounded border transition-colors ${
                                    profLevels.includes(lvl)
                                        ? 'bg-indigo-50 border-indigo-500 text-indigo-700 shadow-sm'
                                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                HSK {lvl}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">ADD/SUBTRACT PROFICIENCY</label>
                    <input
                        type="number"
                        placeholder="e.g. 10 or -10"
                        value={profValue}
                        onChange={(e) => setProfValue(e.target.value)}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                </div>
            </div>
            
            <button
                onClick={handleSetProficiency}
                disabled={profLoading || profLevels.length === 0 || !profValue}
                className="mt-6 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
                {profLoading ? 'Applying...' : 'Apply Proficiency Change'}
            </button>
        </div>
        )}
      </div>

      {/* Weakest Vocab Section — hidden when the user has no records in vocab_proficient2/3 */}
      {allowedVocabVersions.length > 0 && (
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <BookOpen className="w-5 h-5 mr-2 text-red-500" />
              薄弱词汇（熟练度 ≤10）
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              按熟练度排序，每页最多 5 个词。
            </p>
            </div>
            
            {/* Page Size Selector */}
            <div className="mt-4 sm:mt-0 flex items-center space-x-2">
            
            {isAdmin && (
            <button
                onClick={async () => {
                    if (!confirm('Are you sure you want to DELETE ALL proficiency data for this user? This cannot be undone.')) return;
                    try {
                        const res = await api.deleteAdminUserVocabProficiency(id!, token!);
                        if (res.success) {
                            alert('Proficiency data deleted successfully');
                            refreshVocabulary();
                        }
                    } catch (e: any) {
                        console.error(e);
                        alert(e.message || 'Failed to delete proficiency data');
                    }
                }}
                className="mr-4 px-3 py-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-md text-sm font-medium transition-colors flex items-center"
            >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete Proficiency
            </button>
            )}

            <span className="text-sm text-gray-500">每页 5 条</span>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`ml-4 p-2 rounded-md border ${
                showFilters || selectedVersions.length > 0 || selectedLevels.length > 0
                  ? 'bg-primary-50 border-primary-500 text-primary-700'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
              }`}
              aria-label="筛选词汇等级"
              title="Filter Vocabulary"
            >
              <Filter className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-6">
              {/* Version Filter */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">HSK Version</h4>
                <div className="flex space-x-4">
                  {allowedVocabVersions.map((ver) => (
                    <label key={ver} className="inline-flex items-center">
                      <input
                        type="radio"
                        name="vocab-family"
                        checked={selectedVersions.includes(ver)}
                        onChange={() => toggleVersion(ver)}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-600">HSK {ver}.0</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Level Filter */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">HSK Level</h4>
                <div className="flex flex-wrap gap-3">
                  {(examRestrictions ? historyLevels(examRestrictions, selectedVersions[0] + '.0').map(String) : []).map((lvl) => (
                    <label key={lvl} className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedLevels.includes(lvl)}
                        onChange={() => toggleLevel(lvl)}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-600">Level {lvl}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Vocab Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Word
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Meaning
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Proficiency
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stats
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {vocabLoading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-500">Loading vocabulary...</p>
                  </td>
                </tr>
              ) : vocabList.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                    当前分类暂无熟练度 ≤10 的薄弱词汇。
                  </td>
                </tr>
              ) : (
                vocabList.map((item) => (
                  <tr key={`${selectedVersions[0]}-${item.vocab_id}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-lg font-medium text-gray-900">{item.hanzi || '?'}</div>
                      <div className="text-sm text-gray-500">{item.pinyin || ''}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{item.En_meaning || 'No definition'}</div>
                      <div className="text-xs text-gray-400 mt-1">Level {item.level}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-1 w-24 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className={`h-2 rounded-full ${
                              item.proficiency < 0 ? 'bg-red-500' : 
                              item.proficiency <= 10 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(Math.max((item.proficiency + 5) * 10, 5), 100)}%` }}
                          ></div>
                        </div>
                        <span className={`text-sm font-medium ${
                          item.proficiency < 0 ? 'text-red-600' : 'text-gray-900'
                        }`}>
                          {item.proficiency}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>Appeared: {item.appear}</div>
                      <div>Correct: {item.correct}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="bg-white px-4 py-3 border-t border-gray-200 flex items-center justify-between sm:px-6">
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{totalVocab ? (page - 1) * limit + 1 : 0}</span> to <span className="font-medium">{Math.min(page * limit, totalVocab)}</span> of{' '}
                <span className="font-medium">{totalVocab}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={vocabLoading || page === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                    page === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>
                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                  Page {page} of {totalPages || 1}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={vocabLoading || page >= totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                    page === totalPages || totalPages === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
      )}
      </div>
      {/* Reset Modal */}
      {isAdmin && showResetModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete User Exam Records
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete exam records for <span className="font-bold text-gray-900">{user?.username || 'this student'}</span>?
                        This action cannot be undone.
                      </p>
                      
                      <div className="mt-4 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Admin Password
                          </label>
                          <input
                            type="password"
                            value={adminPassword}
                            onChange={(e) => setAdminPassword(e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                            placeholder="Enter admin password to confirm"
                          />
                        </div>

                        <div className="flex items-center">
                          <input
                            id="include-vocab"
                            type="checkbox"
                            checked={includeVocab}
                            onChange={(e) => setIncludeVocab(e.target.checked)}
                            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                          />
                          <label htmlFor="include-vocab" className="ml-2 block text-sm text-gray-900">
                            Also reset vocabulary proficiency stats
                          </label>
                        </div>

                        {resetError && (
                          <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                            {resetError}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={resetLoading || !adminPassword}
                  onClick={handleReset}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm ${
                    (resetLoading || !adminPassword) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {resetLoading ? 'Deleting...' : 'Delete Records'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowResetModal(false);
                    setAdminPassword('');
                    setResetError('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStudentDetail;
